/**
 * Signal shared-number flow (signal-connector.md §5.5) — the counterpart to
 * WhatsApp's StepLink, with one flow difference that drives the whole layout.
 *
 * WhatsApp joins a group from its invite link. Signal **cannot**: a
 * `signal.group` link carries the group master key, and signal-cli-rest-api
 * exposes no route that consumes one (Track D, R-SHARED-JOIN — proven, not
 * assumed). So the customer adds Brain's number to the group on their phone
 * FIRST, and the link they paste afterwards is only a selector: holding it
 * proves access, and it picks one group out of those Brain can see.
 *
 * Being added makes Brain a *pending invite*, not a member — it receives nothing
 * until the invite is accepted, which the server does at bind time. So a matched
 * group can come back `pending`, and the CTA says what will happen rather than
 * pretending the group is already live.
 *
 * That inverts the order of the WhatsApp screen — the number to add comes first
 * and is copyable, the paste box second — and it makes `not_a_member` (404) an
 * expected step rather than a failure: it means "you haven't added it yet",
 * which is where every customer starts. It is shown inline as an instruction,
 * never as an error toast.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, Phone, Trash2, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { CopyField } from '@/components/patterns/copy-field';
import { IconTile } from '@/components/patterns/icon-tile';
import { InfoNote } from '@/components/patterns/info-note';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, type SignalGroupPreview, signalSharedApi } from '@/lib/api';
import { qk } from '@/lib/query-client';

interface Props {
  onNext: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  selected: 'Selected',
  active: 'Active',
  removed: 'Removed',
};

/**
 * 404 `not_a_member` = Brain hasn't been added to the group yet (expected).
 * Matched on the error code, not the status alone: bind also 404s on
 * `workspace_not_found`, which is a real fault and must still be reported.
 */
function isNotAMember(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404 && err.message === 'not_a_member';
}

export function StepSignalShared({ onNext }: Props) {
  const qc = useQueryClient();
  const [inviteLink, setInviteLink] = useState('');
  const [preview, setPreview] = useState<SignalGroupPreview | null>(null);
  const [notAMember, setNotAMember] = useState(false);

  const { data: config } = useQuery({
    queryKey: qk.signalSharedConfig,
    queryFn: () => signalSharedApi.config(),
  });

  const { data: groups } = useQuery({
    queryKey: qk.signalGroups,
    queryFn: () => signalSharedApi.groups(),
  });
  const activeGroups = groups ?? [];

  function resetResult() {
    setPreview(null);
    setNotAMember(false);
  }

  const previewMut = useMutation({
    mutationFn: (link: string) => signalSharedApi.preview(link),
    onSuccess: (res) => {
      setNotAMember(false);
      setPreview(res);
    },
    onError: (err) => {
      setPreview(null);
      // Not an error the customer caused — it's the step they haven't done yet.
      setNotAMember(isNotAMember(err));
      if (!isNotAMember(err)) {
        toast.error("Couldn't read that group link. Check it and try again.");
      }
    },
  });

  const bindMut = useMutation({
    mutationFn: (link: string) => signalSharedApi.bind(link),
    onSuccess: (res) => {
      if (res.announced) {
        toast.success(`Linked "${res.name}" — now indexing.`);
      } else {
        // Bound and ingesting; only the consent message failed. Say so, because
        // the group has NOT been told it is being indexed.
        toast.warning(
          `Linked "${res.name}" — but the notice to the group didn't send. Tell members their messages are being indexed.`,
        );
      }
      resetResult();
      setInviteLink('');
      void qc.invalidateQueries({ queryKey: qk.signalGroups });
    },
    onError: (err) => {
      const status = (err as { status?: number }).status;
      // Both 409s, and they mean opposite things to the customer: one is "you
      // can't have this group", the other is "an admin has to let Brain in".
      if (err instanceof ApiError && err.message === 'accept_pending_approval') {
        toast.error(
          "This group requires an admin to approve new members. Approve Brain's request in Signal, then try again.",
        );
      } else if (err instanceof ApiError && err.message === 'accept_unsupported') {
        // Not retryable and not the customer's fault — a signal-cli defect that
        // needs an operator, so "try again" would be a lie.
        toast.error(
          "Signal wouldn't let Brain accept the invite. This is a known bug on our side — contact support and we'll add Brain to the group manually.",
        );
      } else if (status === 409) toast.error('That group is already linked to another workspace.');
      // Removed from the group between preview and bind. Drop the now-false
      // preview rather than contradict it with "Brain isn't in that group".
      else if (isNotAMember(err)) {
        setPreview(null);
        setNotAMember(true);
      } else toast.error('Failed to link the group. Please try again.');
    },
  });

  const unlinkMut = useMutation({
    mutationFn: (chatKey: string) => signalSharedApi.unlink(chatKey),
    onSuccess: () => {
      toast.success('Group unlinked.');
      void qc.invalidateQueries({ queryKey: qk.signalGroups });
    },
    onError: () => toast.error('Failed to unlink.'),
  });

  return (
    <div className="space-y-6">
      {/* Add-the-number form */}
      <div className="rounded-xl border border-border p-8">
        <IconTile tone="neutral">
          <UserPlus className="h-5 w-5" />
        </IconTile>
        <h2 className="mt-4 text-lg font-semibold">Add Brain to your group</h2>
        <p className="mt-1.5 text-[14px] text-muted-foreground">
          Signal group links can&apos;t be used to join a group, so add Brain&apos;s number from
          your phone — then paste the group&apos;s link here to tell Brain which group that was.
          Brain accepts the invite itself when you link it.
        </p>

        {config?.sharedNumber && (
          <div className="mt-6">
            <div className="mb-1.5 flex items-center gap-2">
              <IconTile tone="green" size={38}>
                <Phone className="h-4 w-4" />
              </IconTile>
              <p className="text-[12px] text-faint">Brain&apos;s Signal number</p>
            </div>
            <CopyField value={config.sharedNumber} mono />
            <InfoNote>
              In Signal: open the group → tap the group name → Add members → paste this number.
            </InfoNote>
          </div>
        )}

        {/* Consent notice — R2, must be visible. */}
        <div className="mt-6 rounded-lg border border-info-border bg-info-bg p-4">
          <p className="text-[13.5px] font-semibold text-info">Let your group members know</p>
          <p className="mt-1 text-[13px] text-info/80">
            Once linked, Brain stores and indexes all messages in the group for search. You must
            inform members that their messages are stored.
          </p>
        </div>

        {/* Paste + preview */}
        <div className="mt-6 space-y-1.5">
          <Label htmlFor="signal-invite">Signal group link</Label>
          <div className="flex gap-2">
            <Input
              id="signal-invite"
              placeholder="https://signal.group/#..."
              value={inviteLink}
              onChange={(e) => {
                setInviteLink(e.target.value);
                resetResult();
              }}
              className="font-mono text-xs"
            />
            <Button
              variant="outline"
              disabled={!inviteLink.trim() || previewMut.isPending}
              onClick={() => previewMut.mutate(inviteLink.trim())}
            >
              {previewMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Preview'}
            </Button>
          </div>
          <InfoNote>
            In Signal: open the group → tap the group name → Group link → turn it on → Copy link.
          </InfoNote>
        </div>

        {/* Not added yet — the expected first state, not an error. */}
        {notAMember && (
          <Alert variant="warning" className="mt-6">
            <UserPlus className="h-4 w-4" />
            <AlertTitle>Brain isn&apos;t in that group yet</AlertTitle>
            <AlertDescription>
              Add {config?.sharedNumber ?? "Brain's number"} to the group on your phone, then press
              Preview again.
            </AlertDescription>
          </Alert>
        )}

        {/* Preview result → confirm */}
        {preview && (
          <div className="mt-6 space-y-3 rounded-lg border border-border p-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{preview.name || 'Signal group'}</span>
              <span className="text-sm text-muted-foreground">
                · {preview.memberCount} member{preview.memberCount === 1 ? '' : 's'}
              </span>
            </div>

            {preview.conflict ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Already linked elsewhere</AlertTitle>
                <AlertDescription>
                  This group is linked to another workspace. A group can belong to only one
                  workspace.
                </AlertDescription>
              </Alert>
            ) : preview.alreadyLinked ? (
              <Alert variant="success">
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>Already linked here</AlertTitle>
                <AlertDescription>This group is already linked to your workspace.</AlertDescription>
              </Alert>
            ) : (
              <>
                {/* Added but unaccepted. Brain receives nothing until bind accepts
                    the invite, so say that rather than implying it's already on. */}
                {preview.pending && (
                  <p className="text-[13px] text-muted-foreground">
                    Brain has been invited to this group. Linking it accepts the invite and starts
                    indexing.
                  </p>
                )}
                <Button
                  size="cta"
                  className="w-full"
                  disabled={bindMut.isPending}
                  onClick={() => bindMut.mutate(inviteLink.trim())}
                >
                  {bindMut.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {preview.pending ? 'Accepting invite…' : 'Linking…'}
                    </>
                  ) : preview.pending ? (
                    'Accept invite & start indexing'
                  ) : (
                    'Start indexing this group'
                  )}
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Linked groups */}
      {activeGroups.length > 0 && (
        <div className="rounded-xl border border-border p-6">
          <h3 className="text-base font-semibold">Linked groups</h3>
          <div className="mt-4 space-y-2">
            {activeGroups.map((g) => (
              <div
                key={g.chatKey}
                className="flex items-center justify-between rounded-md border border-border-subtle px-3 py-2"
              >
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  <span className="truncate">{g.name || 'Signal group'}</span>
                  <span className="text-xs text-muted-foreground">
                    · {STATUS_LABEL[g.status] ?? g.status}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Unlink group"
                  disabled={unlinkMut.isPending}
                  onClick={() => unlinkMut.mutate(g.chatKey)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Button size="cta" onClick={onNext} className="w-full" disabled={activeGroups.length === 0}>
        {activeGroups.length === 0 ? 'Link a group to continue' : 'Continue'}
      </Button>
    </div>
  );
}
